import { useEffect, useMemo, useState } from "react";
import { SectionTitle } from "./SectionTitle";
import { buildHistory, filterHistory, formatDateTime, historyCounts, type HistoryFilter } from "./caseRelances";
import type { CaseRelancesState } from "./useCaseRelances";

const FILTERS: Array<{ key: HistoryFilter; label: string }> = [
  { key: "all", label: "Tout" },
  { key: "CRM", label: "CRM" },
  { key: "Vous", label: "Vous" },
  { key: "Client", label: "Client" },
];
const WHO_CLASS = {
  CRM: "bg-indigo-50 text-indigo-700",
  Vous: "bg-orange-50 text-orange-800",
  Client: "bg-teal-50 text-teal-800",
} as const;
const PAGE = 12;

/** Historique des relances du dossier, filtrable Tout / CRM / Vous / Client. */
export function RelancesHistory({ caseId, state }: { caseId: string; state: CaseRelancesState }) {
  const [filter, setFilter] = useState<HistoryFilter>("all");
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setFilter("all");
    setExpanded(false);
  }, [caseId]);

  const entries = useMemo(() => buildHistory(state.cycle, state.logs), [state.cycle, state.logs]);
  const counts = useMemo(() => historyCounts(entries), [entries]);
  const visible = filterHistory(entries, filter);
  const shown = expanded ? visible : visible.slice(0, PAGE);
  const titleId = `relances-history-title-${caseId}`;

  return (
    <section aria-labelledby={titleId} className="px-4 py-4">
      <SectionTitle id={titleId}>Historique des relances</SectionTitle>
      <div role="group" aria-label="Filtrer l’historique" className="mt-2 grid grid-cols-4 gap-0.5 rounded-md bg-gray-100 p-0.5">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setFilter(f.key);
                setExpanded(false);
              }}
              className={`rounded px-1.5 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                active ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {f.label}
              <span className={`ml-1 tabular-nums ${active ? "text-gray-500" : "text-gray-400"}`}>{counts[f.key]}</span>
            </button>
          );
        })}
      </div>

      {state.loading ? (
        <div className="mt-3 space-y-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded bg-gray-100" />
          ))}
        </div>
      ) : state.error ? (
        <p className="mt-3 text-xs text-gray-500">Historique indisponible.</p>
      ) : shown.length === 0 ? (
        <p className="mt-3 rounded-md bg-gray-50 p-3 text-xs text-gray-500">
          {filter === "all" ? "Aucun contact noté pour ce dossier." : "Rien dans ce filtre."}
        </p>
      ) : (
        <ol className="relative mt-3 space-y-3 before:absolute before:bottom-1 before:left-[25px] before:top-1 before:w-px before:bg-gray-200">
          {shown.map((entry) => (
            <li key={entry.id} className="relative grid grid-cols-[52px_minmax(0,1fr)] items-start gap-2.5">
              <span className={`relative rounded px-1 py-0.5 text-center text-[11px] font-semibold ${WHO_CLASS[entry.who]}`}>{entry.who}</span>
              <span className="min-w-0">
                <span className={`block break-words text-xs ${entry.failed ? "text-red-700" : "text-gray-900"}`}>{entry.text}</span>
                <span className="block text-[11px] text-gray-500">{formatDateTime(entry.at)}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
      {!state.loading && visible.length > PAGE && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 rounded text-xs font-medium text-indigo-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          {expanded ? "Voir moins" : `Voir les ${visible.length - PAGE} plus anciens`}
        </button>
      )}
    </section>
  );
}
